import { ChangeDetectorRef, Component, inject, OnDestroy } from '@angular/core';
import { provideNativeDateAdapter } from '@angular/material/core';
import { DynamicFormArray, FIELD_TYPES, FieldOption, Props } from '../../model/form.model';
import { PageContainerComponent } from '../../layout/page-container/page-container.component';
import { RouterModule } from '@angular/router';
import { AseCerStatusFormArray, AseCerStatusTableSchema, AseCerStatusTitle, AseCertExportSearchConfig, AseLexusCertificationStatusSearchConfig } from '../../model/ase-crt-status.model';
import { LAYOUT_TYPE } from '../../model/common.model';
import { API_STATUS, ApiEnvNames, ERROR_RESPONSE, ExportConfig, SearchConfig } from '../../model/api.model';
import { CommonUtils } from '../../utils/common.utils';
import { BaseComponent } from '../base/base.component';
import { DataTableSchema } from '../../model/dynamic-data-table.model';
import { CommonService } from '../../services/common.service';
import { PaginatorService } from '../../services/paginator-service.service';
import { Subject, takeUntil } from 'rxjs';
import { CSV_EXTENSION, ExcelExportService } from '../../services/excel-export.service';
import { DropdownOptions, FieldNames } from '../../model/form-fields.model';
import { FormService } from '../../services/form.service';
import { FormGroup } from '@angular/forms';
import { AseDropdownSearchConfig } from '../../model/ase-lexus.model';

@Component({
  selector: 'app-ase-lexus-detail',
  standalone: true,
  providers: [provideNativeDateAdapter()],
  imports: [PageContainerComponent, RouterModule],
  templateUrl: './ase-lexus-detail.component.html',
  styleUrl: './ase-lexus-detail.component.scss',
})
export class AseLexusDetailComponent extends BaseComponent implements OnDestroy {
  protected data = 'AseLexusDetailComponent';
  public dataTableSchema: DataTableSchema = structuredClone(AseCerStatusTableSchema);
  public filterSchema: DynamicFormArray = structuredClone(AseCerStatusFormArray);
  public props: Props = {};
  title: string = AseCerStatusTitle;
  readonly LayoutType: typeof LAYOUT_TYPE = LAYOUT_TYPE;
  private readonly commonUtils = inject(CommonUtils);
  service = inject(CommonService);
  changeRef = inject(ChangeDetectorRef);
  paginatorService = inject(PaginatorService);
  excelService = inject(ExcelExportService)
  formService = inject(FormService);
  lexusForm: FormGroup | undefined;
  private isFirstLoad = true;
  isDataInitialized = false;
  currentPage: any;
  public tableFilter = '';
  readonly destroy$ = new Subject<void>();
  public regions:Array<FieldOption> = []
  override ngOnInit() {
    super.ngOnInit();
    super.commonFunction();
    this.dataTableSchema.filterCallback = this.paginationFilterCallback
    this.dataTableSchema.exportCallback = this.exportCallback
    this.dataTableSchema.sortCallback = this.sortCallback;
    this.formService.form$.subscribe(form => {
      this.lexusForm = form;
      this.dynamicFieldValidation(form);
    });
    this.paginatorService.pageEvent$.pipe(takeUntil(this.destroy$)).subscribe(event => {
      if (this.isFirstLoad) {
        this.isFirstLoad = false;
        if (this.dataTableSchema['pageSizeOptions']['pageSize']) {
          this.dataTableSchema['pageSizeOptions']['pageLength'] =
            this.dataTableSchema['pageSizeOptions']['pageSize'][0];
        }
      }
      else {
        this.currentPage = event;
        this.dataTableSchema.dataSource = [];
        this.setTableLoadingState(true);
        this.onSearchClicked('');
      }
    });
    this.props.isrouterGap = true;
  }
  override  onSearchClicked = (searchParam: any) => {
    if (searchParam) {
      this.resetSorting()
      this.resetDataTable();
    }
    const searchConfig = this.getSearchConfig();
    this.fetchAllData(searchConfig)
  };
  exportCallback = () => {
    const searchConfig = this.getSearchConfig();
    searchConfig['body']['download'] = true
    this.fetchAllData(searchConfig, true)
  }
  sortCallback = (fieldInfo: Array<Record<string, string>>) => {
    if (fieldInfo) {
      const sortBy: any = {}
      fieldInfo.forEach(field => {
        sortBy[field['id']] = field['direction']
      })
      this.sortBy = sortBy;
      if (this.dataTableSchema.dataSource.length > 0) {
        const searchConfig = this.getSearchConfig();
        this.resetDataTable()
        this.fetchAllData(searchConfig);
      }
    }
  }
  paginationFilterCallback = (filterValue: string) => {
    if (this.isDataInitialized) {
      if (filterValue || (!filterValue && this.tableFilter)) {
        const searchConfig = this.getSearchConfig();
        this.tableFilter = filterValue;
        this.resetDataTable();
        this.fetchAllData(searchConfig)
      }
    }
  }

  getSearchConfig = () => {
    const searchConfig = structuredClone(AseLexusCertificationStatusSearchConfig)
    this.filterSchema['FieldsArray'].forEach(field => {
      if (field['selectedValue'] && field['selectedValue'] !== null && field['selectedValue'] !== '') {
        if (Number.isNaN(field['selectedValue']) || Array.isArray(field['selectedValue'])) {
          searchConfig['body'][field['fieldName']] = field['selectedValue']
        } else {
          searchConfig['body'][field['fieldName']] = field['selectedValue'].toString();
        }
      } else if (field['fieldType'] === FIELD_TYPES.NUMBER) {
        searchConfig['body'][field['fieldName']] = '';
      }
    });
    if (this.dataTableSchema.inputFilter.length > 0) {
      searchConfig['body']['searchText'] = this.dataTableSchema.inputFilter
    }
    if (this.sortBy && Object.keys(this.sortBy).length > 0) {
      if (!searchConfig['body']) {
        searchConfig['body'] = {}
      }
      searchConfig['body']['sortBy'] = this.sortBy;
    }
    return searchConfig
  }

  fetchAllData = (searchParam: SearchConfig, callFromExport = false) => {
    this.setTableLoadingState(true);
    if (!callFromExport) {
      searchParam['body'].page = Number(this.currentPage?.pageIndex ?? 0) + 1;
      searchParam['body'].pageSize = Number(this.currentPage?.pageSize);
    }
    this.service.getDataFromDataSource(searchParam, ApiEnvNames['AseReport']).then(async response => {
      if (response['status'] === API_STATUS['Success'] && Array.isArray(response.result)) {
        if (callFromExport) {
          const exportConfig: ExportConfig = {
            tableSchema: this.dataTableSchema,
            environment: ApiEnvNames['StaffMaster'],
            fileExtension: CSV_EXTENSION,
            searchParameters: AseCertExportSearchConfig,
            exportFileName: 'ASE_Certifcation_Status',
          };
          await this.service.downloadExcelFromS3(response.result[0], exportConfig);
        }else {
          this.dataTableSchema['pageSizeOptions']['pageLength'] = response?.result?.[0]?.totalRecordsCount ?? 0;
          this.dataTableSchema.dataSource = response.result;
          if (!this.isDataInitialized && response.result.length > 0) {
            this.isDataInitialized = true;
          }
          this.props.updatedOn = response?.result[0] ? response.result[0].createdOn : '';
        }
      }
      else {
        this.dataTableSchema.dataSource = [];
        this.commonUtils.showMatSnackBar(ERROR_RESPONSE['errorFetch'])
      }
      this.changeRef.detectChanges()
    }).catch(err => {
      this.dataTableSchema.dataSource = [];
      console.info(ERROR_RESPONSE['layoutErrorPrefix'], err);
    }).finally(() => {
      this.setTableLoadingState(false);
    })
  };

  override onResetClicked = () => {
  const areaField = this.filterSchema.FieldsArray.find(x => x.fieldName === FieldNames['Area']);
    if (areaField) {
      areaField['displayName'] = 'Area / Region';
      areaField['options'] = [...DropdownOptions[FieldNames['Area']],...this.regions];
    }
    this.filterSchema.FieldsArray = this.filterSchema.FieldsArray.map((field) => ({ ...field, selectedValue: field['defaultValue'] ? field['defaultValue'] : '' }));
    this.dataTableSchema.inputFilter = ''
    this.resetSorting()
    this.resetDataTable();
    this.checkForAnyEmptyFields();
    this.isDataInitialized = false;
  };
  resetDataTable() {
    this.dataTableSchema.dataSource = [];
    if (this.dataTableSchema?.pageSizeOptions) {
      if (this.dataTableSchema?.pageSizeOptions.pageSize) {
        this.currentPage = {
          page: 0,
          pageSize: Number(this.currentPage?.pageSize) || this.dataTableSchema?.pageSizeOptions.pageSize[0],
        };
      }
    }
    this.dataTableSchema['pageSizeOptions']['pageLength'] = 0;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  override updateOptions = async () => {
    const area = this.filterSchema.FieldsArray.find(f => f.fieldName === FieldNames['Area']);
    try {
      const dropDownSearchConfig = structuredClone(AseDropdownSearchConfig);
      const res = await this.service.getDataFromDataSource(dropDownSearchConfig, ApiEnvNames['AseReport']);

      if (res?.status === API_STATUS['Success']) {
        const regions = res.result?.[0]?.toyotaregions || [];
        if (regions.length) {
          this.regions = regions.map((x: { regionCode: any; regionName: any; }) => ({
            fieldValue: x.regionCode,
            fieldLabel: x.regionName
          }));
          const brand = this.filterSchema.FieldsArray.find(f => f.fieldName === FieldNames['Brand']);
          this.applyAreaFieldConfig(area, brand);
        }
      }
    } catch (error) {
      console.info('Error updating options:', error);
    } finally {
      if (area) {
        area.isLoading = false;
      }
    }
  };

  private applyAreaFieldConfig(area: any, brand: any): void {
      if (!area) { return; }
    const brands: string[] = brand?.selectedValue || [];
    if (brands.includes('LEXUS') && brands.includes('TOYOTA')) {
        area.displayName = 'Area / Region';
        area.options = [
          ...DropdownOptions[FieldNames['Area']],
          ...this.regions
        ];
    } else if (brands.includes('TOYOTA')) {
        area.displayName = 'Region';
        area.options = this.regions;
    } else if (brands.includes('LEXUS')) {
        area.displayName = 'Area';
        area.options = DropdownOptions[FieldNames['Area']];
    } else {
        area.displayName = 'Area / Region';
        area.options = [
          ...DropdownOptions[FieldNames['Area']],
          ...this.regions
        ];
    }
  }
  dynamicFieldValidation(form: FormGroup) {
    form.get(FieldNames['Brand'])?.valueChanges.subscribe(value => {
      const area = this.filterSchema.FieldsArray.find(field => field['fieldName'] === FieldNames['Area']);
      if (!area) {return};
      const isLexus = value?.includes('LEXUS');
      const isToyota = value?.includes('TOYOTA');
      if (isLexus && isToyota) {
        area.displayName = 'Area / Region';
        area.options = [
          ...DropdownOptions[FieldNames['Area']],
          ...this.regions
        ];
      } else if (isToyota) {
        area.displayName = 'Region';
        area.options = this.regions;
    } else if (isLexus) {
        area['displayName'] = 'Area';
        area.options = DropdownOptions[FieldNames['Area']];
      } else{
        area['displayName'] = 'Area / Region'
        area['options'] = [...DropdownOptions[FieldNames['Area']], ...structuredClone(this.regions)]
      }
      const selectedValues: string[] = area.selectedValue || [];
      const validValues = selectedValues.filter(val =>
        (area.options || []).some(opt => opt.fieldValue === val)
      );
      if (validValues.length !== selectedValues.length) {
        area.selectedValue = [];
      }
    });
  }
}
